import { Vue } from 'vue-property-decorator'
import { apiClient } from '@/store/apis/apiClient'
import config from '@/config/config'

import {
  CompanyGeneralRequestParam,
  CompanyGeneralData,
  CompanyAddToolData,
  ToolCompanyMasterGetParam,
  ToolCompanyMasterData,
  ToolCompanyDetailGetParam,
  ToolCompanyMasterAddParam,
  ToolCompanyDetailUpdateParam,
  ToolCompanyDetailDeleteParam,
  ToolUserInfoGetParam,
  ToolUserInfoAddParam,
  ToolUserInfoUpdateParam,
  ToolUserInfoDeleteParam,
  ToolManagementTargetGetParam,
  ToolAnnouncementAddParam,
  ToolAnnouncementUpdateParam,
  ToolManagementUserInfoDeleteParam,
  ToolManagementUserInfoAddParam,
  ToolManagementUserData,
  ToolDataRecovery,
  DataRecoveryParam,
  ToolSigfoxInfoSearchParam,
  ToolSigfoxInfoList,
  ToolOptionSetParam
} from '@/models'

/**
 * 会社汎用定義を取得する。
 *
 * @param {CompanyGeneralRequestParam} reqParams 会社汎用定義タイプリスト
 * @returns {Promise<CompanyGeneralData>} 会社汎用定義データ
 */
export async function getCompanyGeneralData(reqParams: CompanyGeneralRequestParam): Promise<CompanyGeneralData> {
  Vue.$log.debug('apis > getCompanyGeneralData: reqParams=', reqParams)
  const res = await apiClient.post(config.api.getCompanyGeneralData, reqParams)

  return res.data.data as CompanyGeneralData
}

/**
 * 会社情報を取得する。
 *
 * @async
 * @param {ToolCompanyMasterGetParam} reqParams パラメータ
 * @returns {Promise<CompanyAddToolData>} 会社情報
 */
export async function getToolCompanyInfoData(reqParams?: ToolCompanyMasterGetParam): Promise<CompanyAddToolData> {
  const res = await apiClient.post(config.api.getCompanyInfoListData, reqParams)
  Vue.$log.debug('apis > getToolCompanyInfoData1: res=', res)
  return res.data.data as CompanyAddToolData
}

/**
 * 会社情報新規登録。
 *
 * @async
 * @param {ToolCompanyMasterAddParam} companyInfo 会社情報新規登録のパラメータ
 * @returns {Promise<void>}
 */
export async function addCompanyInfo(companyInfo: ToolCompanyMasterAddParam): Promise<void> {
  await apiClient.post(config.api.addCompanyInfo, companyInfo)
}

/**
 * 会社情報詳細を取得する。
 *
 * @async
 * @param {ToolCompanyDetailGetParam} reqParams パラメータ
 * @returns {Promise<ToolCompanyMasterData>} 会社情報詳細
 */
export async function getCompanyDetail(reqParams?: ToolCompanyDetailGetParam): Promise<ToolCompanyMasterData> {
  const res = await apiClient.get(config.api.getCompanyDetail, {
    params: reqParams
  })

  return res.data.data as ToolCompanyMasterData
}

/**
 * 会社情報詳細編集。
 *
 * @async
 * @param {ToolCompanyDetailUpdateParam} companyInfo 会社情報編集のパラメータ
 * @returns {Promise<void>}
 */
export async function updateCompanyInfo(companyInfo: ToolCompanyDetailUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateCompanyInfo, companyInfo)
}

/**
 * 会社情報オプション設定。
 *
 * @async
 * @param {ToolOptionSetParam} companyInfo 会社情報オプション設定のパラメータ
 * @returns {Promise<void>}
 */
export async function setConnectOptionInfo(companyInfo: ToolOptionSetParam): Promise<void> {
  await apiClient.post(config.api.setConnectOptionInfo, companyInfo)
}

/**
 * 会社情報削除。
 *
 * @async
 * @param {ToolCompanyDetailDeleteParam} companyId 会社情報削除のパラメータ
 * @returns {Promise<void>}
 */
export async function deleteCompanyInfo(companyId: ToolCompanyDetailDeleteParam): Promise<void> {
  await apiClient.post(config.api.deleteCompanyInfo, companyId)
}

/**
 * ユーザを取得する。
 *
 * @async
 * @param {ToolUserInfoGetParam} reqParams パラメータ
 * @returns {Promise<CompanyAddToolData>} ユーザ
 */
export async function getToolUserInfoData(reqParams: ToolUserInfoGetParam): Promise<CompanyAddToolData> {
  const res = await apiClient.get(config.api.getUserInfoListData, {
    params: reqParams
  })
  Vue.$log.debug('apis > getToolUserInfoData: res=', res)
  return res.data.data as CompanyAddToolData
}

/**
 * ユーザ追加。
 *
 * @async
 * @param {ToolUserInfoAddParam} userInfo ユーザ追加のパラメータ
 * @returns {Promise<void>}
 */
export async function addUserInfo(userInfo: ToolUserInfoAddParam): Promise<void> {
  await apiClient.post(config.api.addUserInfo, userInfo)
}

/**
 * ユーザ編集。
 *
 * @async
 * @param {ToolUserInfoUpdateParam} userInfo ユーザ編集のパラメータ
 * @returns {Promise<void>}
 */
export async function updateUserInfo(userInfo: ToolUserInfoUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateUserInfo, userInfo)
}

/**
 * ユーザ削除。
 *
 * @async
 * @param {ToolUserInfoDeleteParam} deleteReqParams ユーザ削除のパラメータ
 * @returns {Promise<void>}
 */
export async function deleteUserInfo(deleteReqParams: ToolUserInfoDeleteParam): Promise<void> {
  await apiClient.post(config.api.deleteUserInfo, deleteReqParams)
}

/**
 * 管理対象一覧を取得する。
 *
 * @async
 * @param {ToolManagementTargetGetParam} reqParams パラメータ
 * @returns {Promise<CompanyAddToolData>} ユーザ
 */
export async function getManagementTargetListData(
  reqParams: ToolManagementTargetGetParam
): Promise<CompanyAddToolData> {
  const res = await apiClient.post(config.api.getManagementTargetListData, reqParams)
  Vue.$log.debug('apis > getManagementTargetListData: res=', res)
  return res.data.data as CompanyAddToolData
}

/**
 * お知らせを取得する。
 *
 * @async
 * @returns {Promise<CompanyAddToolData>} お知らせ
 */
export async function getAnnouncementListData(): Promise<CompanyAddToolData> {
  const res = await apiClient.get(config.api.getAnnouncementListData)

  return res.data.data as CompanyAddToolData
}

/**
 * ログイン通知の取得する。
 *
 * @async
 * @returns {Promise<CompanyAddToolData>} お知らせ
 */
export async function getAnnouncementLoginListData(): Promise<CompanyAddToolData> {
  const res = await apiClient.get(config.api.getAnnouncementLoginListData)

  return res.data.data as CompanyAddToolData
}

/**
 * お知らせ追加。
 *
 * @async
 * @param {ToolAnnouncementAddParam} announce お知らせ追加のパラメータ
 * @returns {Promise<void>}
 */
export async function addAnnounce(announce: ToolAnnouncementAddParam): Promise<void> {
  await apiClient.post(config.api.addAnnounce, announce)
}

/**
 * お知らせ編集。
 *
 * @async
 * @param {ToolAnnouncementUpdateParam} announce お知らせ編集のパラメータ
 * @returns {Promise<void>}
 */
export async function updateAnnounce(announce: ToolAnnouncementUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateAnnounce, announce)
}

/**
 * CSVアップロード
 *
 * @async
 * @param {FormData} uploadParam アップロードCSV
 * @returns {Promise<void>}
 */
export async function uploadToolCSV(uploadParam: FormData): Promise<void> {
  await apiClient.post(config.api.uploadToolCSV, uploadParam)
}

/**
 * 会社ユーザを取得する。
 *
 * @async
 * @returns {Promise<ToolManagementUserData>} ユーザ
 */
export async function getToolManagementUserInfoData(): Promise<ToolManagementUserData> {
  const res = await apiClient.get(config.api.getManagementUserInfoListData)
  Vue.$log.debug('apis > getToolManagementUserInfoData: res=', res)
  return res.data.data as ToolManagementUserData
}

/**
 * 会社ユーザ追加。
 *
 * @async
 * @param {ToolManagementUserInfoAddParam} userInfo ユーザ追加のパラメータ
 * @returns {Promise<void>}
 */
export async function addManagementUserInfo(userInfo: ToolManagementUserInfoAddParam): Promise<void> {
  await apiClient.post(config.api.addManagementUserInfo, userInfo)
}

/**
 * 会社ユーザ編集。
 *
 * @async
 * @param {ToolManagementUserInfoAddParam} userInfo ユーザ編集のパラメータ
 * @returns {Promise<void>}
 */
export async function updateManagementUserInfo(userInfo: ToolManagementUserInfoAddParam): Promise<void> {
  await apiClient.post(config.api.updateManagementUserInfo, userInfo)
}

/**
 * 会社ユーザ削除。
 *
 * @async
 * @param {ToolManagementUserInfoDeleteParam} deleteReqParams ユーザ削除のパラメータ
 * @returns {Promise<void>}
 */
export async function deleteManagementUserInfo(deleteReqParams: ToolManagementUserInfoDeleteParam): Promise<void> {
  await apiClient.post(config.api.deleteManagementUserInfo, deleteReqParams)
}

/**
 * MFA認証リセット
 *
 * @async
 * @param {ToolManagementUserInfoDeleteParam} deleteReqParams ユーザ削除のパラメータ
 * @returns {Promise<void>}
 */
export async function mfaReset(deleteReqParams: ToolManagementUserInfoDeleteParam): Promise<void> {
  await apiClient.post(config.api.mfaReset, deleteReqParams)
}

/**
 * データ復旧を取得する。
 *
 * @async
 * @returns {Promise<ToolDataRecovery[]>} データ
 */
export async function getToolDataRecoverData(): Promise<ToolDataRecovery[]> {
  const res = await apiClient.get(config.api.getToolDataRecoverData)
  Vue.$log.debug('apis > getToolDataRecoverData: res=', res)
  return res.data.data.list as ToolDataRecovery[]
}

/**
 * データ復旧実行
 *
 * @async
 * @param {DataRecoveryParam} param 実行のパラメータ
 * @returns {Promise<void>} データ
 */
export async function postDataRecovery(param: DataRecoveryParam): Promise<void> {
  await apiClient.post(config.api.postDataRecovery, param)
}

/**
 * 端末一覧を取得する。
 *
 * @async
 * @param {ToolSigfoxInfoSearchParam} reqParams パラメータ
 * @returns {Promise<ToolSigfoxInfoList>} 端末一覧
 */
export async function getToolCompanySigfoxInfoData(reqParams?: ToolSigfoxInfoSearchParam): Promise<ToolSigfoxInfoList> {
  const res = await apiClient.post(config.api.getSigfoxInfoListData, reqParams)
  Vue.$log.debug('apis > getToolCompanySigfoxInfoData: res=', res)
  return res.data.data as ToolSigfoxInfoList
}
